<script lang="ts">
  import Typography from "$lib/components/Typography/Typography.svelte";
  import RecommendationCarousel from "$lib/components/Nosto/RecommendationCarousel.svelte";
  import type { PageType } from "$lib/stores/recommendations";
  import { CarouselSize } from "$lib/gql/genCms/codegen";
  import { onMount, tick } from "svelte";

  type CarouselTab = {
    pageType: PageType;
    input: Record<string, any>;
    carouselSize: CarouselSize;
    tabTitle: string;
    tabDescription?: string;
  };

  export let carousels: (CarouselTab & { displayTitle: boolean })[] = [];
  let selectedTabIndex: number = 0;
  let selectedTab: CarouselTab | null = carousels[selectedTabIndex] || null;

  let carouselDivs: HTMLDivElement[] = [];
  let height: string = "auto";

  $: (selectedTab = carousels[selectedTabIndex]), selectedTabIndex;

  const updateHeight = async (tabIndex: number, divs: HTMLDivElement[]) => {
    await tick(); // Wait for DOM updates

    if (divs[tabIndex]) {
      const activeDiv = divs[tabIndex];
      const h = activeDiv.scrollHeight;
      height = `${h}px`;
    }
  };

  $: updateHeight(selectedTabIndex, carouselDivs), selectedTabIndex;

  onMount(() => {
    updateHeight(selectedTabIndex, carouselDivs);
  });
</script>

<style lang="scss">
  .tabbed-recommendation-container {
    width: 100%;
    padding-top: $spacingSm;
    padding-bottom: $spacingSm;

    @include breakpoint("md") {
      padding-top: calc(2 * $spacingMd);
      padding-bottom: $spacingMd;
    }
    @include breakpoint("xl") {
      padding-top: calc(2 * $spacingLg);
      padding-bottom: $spacingLg;
    }
  }

  .tab-headers {
    padding-left: $inlineIconLeftSm;

    @include breakpoint("md") {
      padding-left: $inlineIconLeftMd;
    }
    @include breakpoint("lg") {
      padding-left: $inlineIconLeftLg;
    }
    @include breakpoint("xl") {
      padding-left: $inlineIconLeftXl;
    }
    @include breakpoint("xxl") {
      padding-left: $inlineIconLeftXxl;
    }

    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    align-items: center;
    gap: calc(2 * $spacingSm);
  }

  .tab-button {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;

    &:hover {
      text-decoration: underline;
    }
  }

  .tabs {
    height: var(--carousel-height);
    display: flex;
    flex-direction: row;
    overflow: hidden;
    transition:
      height 0.3s ease-in-out,
      transform 0.3s ease-in-out; // Add height transition
  }

  .tab-item {
    width: 100%; // Full width
    flex-shrink: 0; // Prevent shrinking
    opacity: 0; // Hide by default
    transition:
      opacity 0.3s ease-in-out,
      transform 0.3s ease-in-out; // Smooth transition
    transform: translateX(calc(-100% * var(--selected-tab-index, 0)));

    &.active {
      opacity: 1;
    }
  }
  :global(
      div.tabbed-recommendation-container
        > div.recommendation-carousel
        > div.carousel--padded
    ) {
    padding: 0;
  }
  :global(
      div.tabbed-recommendation-container
        > div.recommendation-carousel
        > div
        > div
    ) {
    padding: 0;
  }

  :global(
      div.tabbed-recommendation-container
        > div.tabs
        > div.tab-item.active
        > div.no-nosto-title
        > div
        > div
        > div
        > div.carousel--header
    ) {
    padding-bottom: $spacingSm;
    @include breakpoint("md") {
      padding-bottom: $spacingMd;
    }
    @include breakpoint("lg") {
      padding-bottom: $spacingLg;
    }
  }
  :global(
      div.tabbed-recommendation-container
        > div.tabs
        > div.tab-item.active
        > div.no-nosto-title
        > div
        > div.carousel.carousel--padded
    ) {
    padding: $spacingSm 0;
    @include breakpoint("md") {
      padding: $spacingMd 0;
    }
    @include breakpoint("lg") {
      padding: $spacingLg 0;
    }
  }
</style>

<div class="tabbed-recommendation-container">
  <div class="tab-headers">
    {#each carousels as c, index}
      <div class="tab-header">
        <button class="tab-button" on:click={() => (selectedTabIndex = index)}>
          <Typography
            component="h3"
            variant="h3-2"
            theme={selectedTabIndex === index ? "on-surface" : "on-surface-30"}
            underline={selectedTabIndex === index}
          >
            {c.tabTitle}
          </Typography>
        </button>
      </div>
    {/each}
  </div>
  <div
    style={`--carousel-height: ${height}; --selected-tab-index: ${selectedTabIndex};`}
    class="tabs"
  >
    {#each carousels as c, index (JSON.stringify(c))}
      <div class="tab-item {selectedTabIndex === index ? 'active' : ''}">
        <div
          bind:this={carouselDivs[index]}
          class={c.displayTitle ? "" : "no-nosto-title"}
        >
          <RecommendationCarousel
            pageType={c.pageType}
            input={c.input}
            carouselSize={c.carouselSize}
            on:show={() => {
              if (index === selectedTabIndex) {
                updateHeight(selectedTabIndex, carouselDivs);
              }
            }}
            displayTitle={c.displayTitle}
          />
        </div>
      </div>
    {/each}
  </div>
</div>
